import {
  View,
  Text,
  TextInput,
  Pressable,
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  Modal,
} from 'react-native';
import { useState, useRef, useCallback } from 'react';
import { trpc } from '../lib/trpc';
import { useColors } from '../lib/colors';
import { AiDataSharingConsent } from './AiDataSharingConsent';
import { MarkdownMessage } from './MarkdownMessage';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: string[];
}

interface AiAssistFABProps {
  /** Pre-filled suggestion shown as a chip the user can tap */
  suggestion: string;
  /** Optional richer prompt sent to the API while keeping the chip text concise */
  prompt?: string;
}

// ─── Tool name formatting ─────────────────────────────────────────────────────

const TOOL_LABELS: Record<string, string> = {
  getFinancialSummary: 'Looking up your financial summary\u2026',
  getTransactions: 'Fetching transactions\u2026',
  getBudgetVsActual: 'Comparing budget vs. actual\u2026',
  getFirePlanAnalysis: 'Analyzing your FIRE plan results\u2026',
  runFireScenario: 'Running FIRE scenario\u2026',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  const c = useColors();
  return (
    <View className={`max-w-[85%] ${isUser ? 'self-end' : 'self-start'}`}>
      {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
        <View className="mb-1.5 gap-1">
          {message.toolCalls.map((toolName) => (
            <View
              key={toolName}
              className="flex-row items-center gap-1.5 self-start bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full"
            >
              <ActivityIndicator size="small" color={c.primary} />
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {TOOL_LABELS[toolName] ?? `Using ${toolName}\u2026`}
              </Text>
            </View>
          ))}
        </View>
      )}
      <View
        className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-forest-600 rounded-tr-sm'
            : 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 shadow-sm rounded-tl-sm'
        }`}
      >
        <MarkdownMessage content={message.content} isUser={isUser} />
      </View>
    </View>
  );
}

function TypingIndicator({ toolCalls }: { toolCalls: string[] }) {
  const c = useColors();
  if (toolCalls.length > 0) {
    return (
      <View className="self-start max-w-[85%] gap-1 mb-2">
        {toolCalls.map((toolName) => (
          <View
            key={toolName}
            className="flex-row items-center gap-1.5 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-full"
          >
            <ActivityIndicator size="small" color={c.primary} />
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              {TOOL_LABELS[toolName] ?? `Using ${toolName}\u2026`}
            </Text>
          </View>
        ))}
      </View>
    );
  }
  return (
    <View className="self-start bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm mb-2">
      <View className="flex-row gap-1 items-center">
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
        <View className="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full" />
      </View>
    </View>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function AiAssistFAB({ suggestion, prompt }: AiAssistFABProps) {
  const [visible, setVisible] = useState(false);
  const listRef = useRef<FlatList>(null);
  const utils = trpc.useUtils();

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content:
        "Hi! I\u2019m here to help with this step. Ask me anything or tap the suggestion below.",
    },
  ]);

  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const streamingTextRef = useRef('');
  const [activeToolCalls, setActiveToolCalls] = useState<string[]>([]);
  const householdQ = trpc.households.get.useQuery();
  const setAiDataSharingConsentMutation = trpc.households.setAiDataSharingConsent.useMutation({
    onSuccess: () => {
      void utils.households.get.invalidate();
    },
  });
  const [apiMessages, setApiMessages] = useState<
    { role: 'user' | 'assistant'; content: string }[]
  >([]);
  const hasAiDataSharingConsent = householdQ.data?.hasAiDataSharingConsent ?? false;
  const selectedAiProvider = householdQ.data?.aiProvider ?? 'google';
  const aiConsentError = setAiDataSharingConsentMutation.error?.message ?? null;

  // ─── Subscription ─────────────────────────────────────────────────────────

  trpc.ai.chat.useSubscription(
    { messages: apiMessages },
    {
      enabled: hasAiDataSharingConsent && isStreaming && apiMessages.length > 0,
      onData(event) {
        switch (event.type) {
          case 'delta':
            setStreamingText((prev) => {
              const next = prev + event.text;
              streamingTextRef.current = next;
              return next;
            });
            setActiveToolCalls([]);
            setTimeout(
              () => listRef.current?.scrollToEnd({ animated: false }),
              50,
            );
            break;

          case 'tool_call':
            setActiveToolCalls((prev) =>
              prev.includes(event.toolName) ? prev : [...prev, event.toolName],
            );
            break;

          case 'tool_result':
            setActiveToolCalls((prev) =>
              prev.filter((t) => t !== event.toolName),
            );
            break;

          case 'done': {
            const finalText = streamingTextRef.current;
            const assistantMsg: Message = {
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: finalText,
            };
            setMessages((prev) => [...prev, assistantMsg]);
            setApiMessages((prev) => [
              ...prev,
              { role: 'assistant', content: finalText },
            ]);
            streamingTextRef.current = '';
            setStreamingText('');
            setActiveToolCalls([]);
            setIsStreaming(false);
            break;
          }

          case 'error':
            setMessages((prev) => [
              ...prev,
              {
                id: `error-${Date.now()}`,
                role: 'assistant',
                content: `Sorry, something went wrong: ${event.message}. Please try again.`,
              },
            ]);
            streamingTextRef.current = '';
            setStreamingText('');
            setActiveToolCalls([]);
            setIsStreaming(false);
            break;
        }
      },
      onError(err) {
        const message = err.message?.trim() || 'The AI stream ended unexpectedly';
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `Connection error: ${message}. Please try again.`,
          },
        ]);
        setStreamingText('');
        setActiveToolCalls([]);
        setIsStreaming(false);
      },
    },
  );

  // ─── Send handler ─────────────────────────────────────────────────────────

  const sendMessage = useCallback(
    (payload: string | { displayText: string; apiText?: string }) => {
      const displayText =
        typeof payload === 'string' ? payload : payload.displayText;
      const apiText =
        typeof payload === 'string'
          ? payload
          : payload.apiText ?? payload.displayText;
      const normalizedDisplay = displayText.trim();
      const normalizedApi = apiText.trim();
      if (
        !normalizedDisplay
        || !normalizedApi
        || isStreaming
        || !hasAiDataSharingConsent
      ) return;

      const userMsg: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: normalizedDisplay,
      };
      setMessages((prev) => [...prev, userMsg]);

      const newApiMessages: { role: 'user' | 'assistant'; content: string }[] =
        [...apiMessages, { role: 'user', content: normalizedApi }];
      setApiMessages(newApiMessages);
      setInput('');
      streamingTextRef.current = '';
      setStreamingText('');
      setActiveToolCalls([]);
      setIsStreaming(true);

      setTimeout(
        () => listRef.current?.scrollToEnd({ animated: true }),
        100,
      );
    },
    [apiMessages, hasAiDataSharingConsent, isStreaming],
  );

  const handleSend = useCallback(() => {
    sendMessage(input);
  }, [input, sendMessage]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  const showTyping =
    isStreaming && (streamingText === '' || activeToolCalls.length > 0);
  const showStreamingBubble = isStreaming && streamingText !== '';

  return (
    <>
      {/* FAB */}
      <Pressable
        onPress={() => setVisible(true)}
        className="absolute bottom-24 right-4 w-12 h-12 rounded-full bg-forest-600 items-center justify-center shadow-lg z-50"
        style={{
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.25,
          shadowRadius: 4,
          elevation: 5,
        }}
      >
        <Text className="text-white text-lg">AI</Text>
      </Pressable>

      {/* Modal */}
      <Modal
        visible={visible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setVisible(false)}
      >
        <KeyboardAvoidingView
          className="flex-1 bg-gray-50 dark:bg-gray-900"
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 0}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-4 pt-14 pb-3 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
            <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Ask AI
            </Text>
            <Pressable
              onPress={() => setVisible(false)}
              hitSlop={8}
              className="px-3 py-1.5"
            >
              <Text className="text-sm text-forest-600 dark:text-forest-500 font-medium">Done</Text>
            </Pressable>
          </View>

          {hasAiDataSharingConsent ? (
            <>
              <FlatList
                ref={listRef}
                data={messages}
                keyExtractor={(item) => item.id}
                contentContainerStyle={{ padding: 16, gap: 12 }}
                renderItem={({ item }) => <MessageBubble message={item} />}
                ListFooterComponent={
                  <>
                    {showStreamingBubble && (
                      <View className="self-start max-w-[85%] mt-3">
                        <View className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                          <MarkdownMessage
                            content={streamingText}
                            isUser={false}
                          />
                        </View>
                      </View>
                    )}
                    {showTyping && (
                      <View className="mt-3">
                        <TypingIndicator toolCalls={activeToolCalls} />
                      </View>
                    )}
                    {!isStreaming && messages.length === 1 && (
                      <View className="mt-4">
                        <Text className="text-xs text-gray-400 dark:text-gray-500 mb-2 ml-1">
                          Try asking:
                        </Text>
                        <Pressable
                          onPress={() =>
                            sendMessage({
                              displayText: suggestion,
                              apiText: prompt ?? suggestion,
                            })
                          }
                          className="self-start bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-full px-3 py-1.5"
                        >
                          <Text className="text-xs text-gray-700 dark:text-gray-300">
                            {suggestion}
                          </Text>
                        </Pressable>
                      </View>
                    )}
                  </>
                }
                onContentSizeChange={() =>
                  listRef.current?.scrollToEnd({ animated: false })
                }
              />

              <View className="flex-row items-end gap-2 px-4 pb-8 pt-3 bg-white dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700">
                <TextInput
                  value={input}
                  onChangeText={setInput}
                  placeholder={
                    isStreaming ? 'Responding\u2026' : 'Ask about this step\u2026'
                  }
                  placeholderTextColor="#9ca3af"
                  className="flex-1 rounded-2xl bg-gray-100 dark:bg-gray-700 px-4 py-3 text-base text-gray-800 dark:text-gray-200 max-h-28"
                  returnKeyType="send"
                  onSubmitEditing={handleSend}
                  multiline
                  editable={!isStreaming}
                />
                <Pressable
                  onPress={handleSend}
                  disabled={!input.trim() || isStreaming}
                  className={`rounded-full w-11 h-11 items-center justify-center ${
                    input.trim() && !isStreaming ? 'bg-forest-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  {isStreaming ? (
                    <ActivityIndicator size="small" color="#9ca3af" />
                  ) : (
                    <Text
                      className={`text-lg ${
                        input.trim() ? 'text-white' : 'text-gray-400'
                      }`}
                    >
                      ↑
                    </Text>
                  )}
                </Pressable>
              </View>
            </>
          ) : (
            <View className="flex-1 px-4 py-4">
              <AiDataSharingConsent
                provider={selectedAiProvider}
                loading={setAiDataSharingConsentMutation.isPending}
                error={aiConsentError}
                title="Permission required"
                ctaLabel="Allow Ask AI"
                onAccept={() => {
                  setAiDataSharingConsentMutation.mutate({ granted: true });
                }}
                onDecline={() => setVisible(false)}
              />
            </View>
          )}
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}
